import type { DrillCone } from '@4cm/core/drills/types';

/**
 * SVG primitives for every DrillCone variant — what the equipment
 * overlay renders for each placed item. Each renderer accepts a centre
 * point in normalised [0,1] pitch coords + a unit size (yd → px) so it
 * scales with the parent SVG viewBox.
 */

interface EquipmentSvgProps {
  cone: DrillCone;
  /** Centre point of the equipment in SVG units (already mapped from norm). */
  cx: number;
  cy: number;
  /** Pixels per yard at the current zoom (scales the shape). */
  pxPerYd: number;
  selected?: boolean;
  onPointerDown?: (e: React.PointerEvent<SVGGElement>) => void;
}

const SELECTED_STROKE = '#27AE60';
const NEUTRAL_STROKE = 'rgba(0,0,0,0.55)';

export function EquipmentSvg({ cone, cx, cy, pxPerYd, selected, onPointerDown }: EquipmentSvgProps): React.JSX.Element {
  const variant = cone.variant ?? 'cone';
  const rotation = cone.rotation ?? 0;
  const fill = cone.color ?? defaultFillFor(variant);
  const stroke = selected ? SELECTED_STROKE : NEUTRAL_STROKE;
  const strokeWidth = selected ? 2 : 1;
  const transform = rotation ? `rotate(${rotation} ${cx} ${cy})` : undefined;
  const cursor = onPointerDown ? 'grab' : 'default';
  // Equipment must remain draggable in Select mode even when the
  // EquipmentOverlay's background rect has pointer-events: none.
  const groupStyle: React.CSSProperties = { cursor, pointerEvents: 'auto' };

  switch (variant) {
    case 'flat':
    case 'cone_disc':
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <ellipse cx={cx} cy={cy} rx={0.55 * pxPerYd} ry={0.22 * pxPerYd}
            fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
          {selected && <Halo cx={cx} cy={cy} r={pxPerYd * 1.1} />}
        </g>
      );

    case 'cone':
    case 'cone_training':
    case 'cone_traffic': {
      const h = 0.7 * pxPerYd;
      const w = 0.5 * pxPerYd;
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <ellipse cx={cx} cy={cy + h * 0.5} rx={w * 0.7} ry={w * 0.2} fill="rgba(0,0,0,0.3)" />
          <polygon
            points={`${cx - w / 2},${cy + h * 0.5} ${cx + w / 2},${cy + h * 0.5} ${cx},${cy - h * 0.7}`}
            fill={fill} stroke={stroke} strokeWidth={strokeWidth}
          />
          {selected && <Halo cx={cx} cy={cy} r={pxPerYd * 1.2} />}
        </g>
      );
    }

    case 'mannequin':
    case 'pole': {
      const H = 2.2 * pxPerYd; // taller than a cone — ~2yd / 1.8m
      const W = 0.32 * pxPerYd;
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <ellipse cx={cx} cy={cy + W * 0.3} rx={W * 1.4} ry={W * 0.4} fill="rgba(0,0,0,0.35)" />
          <rect
            x={cx - W / 2}
            y={cy - H}
            width={W}
            height={H * 1.05}
            rx={W * 0.4}
            fill={fill} stroke={stroke} strokeWidth={strokeWidth}
          />
          {selected && <Halo cx={cx} cy={cy - H * 0.5} r={pxPerYd * 1.5} />}
        </g>
      );
    }

    case 'corner_flag': {
      const H = 1.5 * pxPerYd;
      const F = 0.6 * pxPerYd;
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <line x1={cx} y1={cy} x2={cx} y2={cy - H} stroke={stroke} strokeWidth={Math.max(1, pxPerYd * 0.08)} />
          <polygon
            points={`${cx},${cy - H} ${cx + F},${cy - H + F * 0.5} ${cx},${cy - H + F}`}
            fill={fill} stroke={stroke} strokeWidth={strokeWidth}
          />
          {selected && <Halo cx={cx} cy={cy - H * 0.5} r={pxPerYd * 1.2} />}
        </g>
      );
    }

    case 'gate': {
      const W = 1.4 * pxPerYd;
      const s = 0.45 * pxPerYd;
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <line x1={cx - W} y1={cy} x2={cx + W} y2={cy}
            stroke="rgba(255,255,255,0.4)" strokeDasharray="3,3" strokeWidth={1.5} />
          <polygon
            points={`${cx - W - s * 0.5},${cy + s * 0.2} ${cx - W + s * 0.5},${cy + s * 0.2} ${cx - W},${cy - s * 0.7}`}
            fill={fill} stroke={stroke} strokeWidth={strokeWidth}
          />
          <polygon
            points={`${cx + W - s * 0.5},${cy + s * 0.2} ${cx + W + s * 0.5},${cy + s * 0.2} ${cx + W},${cy - s * 0.7}`}
            fill={fill} stroke={stroke} strokeWidth={strokeWidth}
          />
          {selected && <Halo cx={cx} cy={cy} r={pxPerYd * 1.8} />}
        </g>
      );
    }

    case 'goal_mini':
    case 'goal_small':
    case 'goal_7v7':
    case 'goal_9v9':
    case 'goal_full': {
      const W = goalWidthYd(variant) * pxPerYd;
      const H = goalHeightYd(variant) * pxPerYd;
      const postW = Math.max(2, 0.12 * pxPerYd);
      const left = cx - W / 2;
      const right = cx + W / 2;
      const top = cy - H;
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <ellipse cx={cx} cy={cy + 1} rx={W / 2 + 1} ry={postW * 0.6} fill="rgba(0,0,0,0.3)" />
          <rect
            x={left + postW / 2}
            y={top + postW / 2}
            width={W - postW}
            height={H - postW / 2}
            fill="rgba(255,255,255,0.06)"
            stroke="rgba(255,255,255,0.18)"
            strokeWidth={0.5}
            strokeDasharray="2,2"
          />
          <rect x={left} y={top} width={postW} height={H} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
          <rect x={right - postW} y={top} width={postW} height={H} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
          <rect x={left} y={top} width={W} height={postW} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
          {selected && <Halo cx={cx} cy={top + H / 2} r={W / 2 + pxPerYd * 0.4} />}
        </g>
      );
    }

    case 'crossbar': {
      const W = 8 * pxPerYd;
      const T = Math.max(2, 0.15 * pxPerYd);
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <rect x={cx - W / 2} y={cy - T / 2} width={W} height={T} fill={fill}
            stroke={stroke} strokeWidth={strokeWidth} />
          {selected && <Halo cx={cx} cy={cy} r={W / 2 + pxPerYd * 0.3} />}
        </g>
      );
    }

    case 'hurdle_mini':
    case 'hurdle_med': {
      const W = 1.2 * pxPerYd;
      const H = (variant === 'hurdle_med' ? 0.55 : 0.3) * pxPerYd;
      const T = Math.max(1.5, 0.08 * pxPerYd);
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <rect x={cx - W / 2} y={cy - T / 2} width={W} height={T} fill={fill}
            stroke={stroke} strokeWidth={strokeWidth} />
          <line x1={cx - W / 2 + T} y1={cy + T / 2} x2={cx - W / 2 + T} y2={cy + H}
            stroke={stroke} strokeWidth={strokeWidth} />
          <line x1={cx + W / 2 - T} y1={cy + T / 2} x2={cx + W / 2 - T} y2={cy + H}
            stroke={stroke} strokeWidth={strokeWidth} />
          {selected && <Halo cx={cx} cy={cy} r={W / 2 + pxPerYd * 0.3} />}
        </g>
      );
    }

    case 'agility_ladder': {
      const W = 0.6 * pxPerYd;
      const L = 4 * pxPerYd; // 4yd long
      const rungs = 8;
      const rungStep = L / rungs;
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <rect x={cx - W / 2} y={cy - L / 2} width={W} height={L}
            fill="rgba(255,255,255,0.05)"
            stroke={fill} strokeWidth={Math.max(1, 0.06 * pxPerYd)} />
          {Array.from({ length: rungs - 1 }, (_, i) => {
            const y = cy - L / 2 + rungStep * (i + 1);
            return <line key={i} x1={cx - W / 2} y1={y} x2={cx + W / 2} y2={y}
              stroke={fill} strokeWidth={Math.max(1, 0.05 * pxPerYd)} />;
          })}
          {selected && <Halo cx={cx} cy={cy} r={L / 2 + pxPerYd * 0.4} />}
        </g>
      );
    }

    case 'bench': {
      const W = 3 * pxPerYd;
      const H = 0.5 * pxPerYd;
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <rect x={cx - W / 2} y={cy - H / 2} width={W} height={H}
            fill={fill} stroke={stroke} strokeWidth={strokeWidth} rx={2} />
          {selected && <Halo cx={cx} cy={cy} r={W / 2 + pxPerYd * 0.3} />}
        </g>
      );
    }

    case 'rebounder': {
      const W = 1.5 * pxPerYd;
      const H = 1.0 * pxPerYd;
      return (
        <g onPointerDown={onPointerDown} transform={transform} style={groupStyle}>
          <rect x={cx - W / 2} y={cy - H / 2} width={W} height={H}
            fill="rgba(255,255,255,0.05)"
            stroke={fill} strokeWidth={Math.max(1, 0.1 * pxPerYd)} />
          {Array.from({ length: 5 }, (_, i) => {
            const y = cy - H / 2 + (H / 4) * (i + 0.5);
            return <line key={i} x1={cx - W / 2 + 2} y1={y} x2={cx + W / 2 - 2} y2={y}
              stroke={fill} strokeWidth={Math.max(0.5, 0.04 * pxPerYd)} opacity={0.6} />;
          })}
          {selected && <Halo cx={cx} cy={cy} r={W / 2 + pxPerYd * 0.4} />}
        </g>
      );
    }

    default:
      return <circle cx={cx} cy={cy} r={pxPerYd * 0.4} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  }
}

function Halo({ cx, cy, r }: { cx: number; cy: number; r: number }): React.JSX.Element {
  return (
    <>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={SELECTED_STROKE} strokeWidth={2.5} opacity={0.9} />
      <circle cx={cx} cy={cy} r={r * 1.15} fill="none" stroke="#7fbf7f" strokeWidth={1} opacity={0.5} />
    </>
  );
}

function defaultFillFor(variant: NonNullable<DrillCone['variant']>): string {
  if (variant.startsWith('goal') || variant === 'crossbar') return '#FFFFFF';
  if (variant === 'mannequin' || variant === 'pole') return '#95A5A6';
  if (variant === 'corner_flag') return '#E74C3C';
  if (variant === 'agility_ladder' || variant === 'rebounder') return '#1ABC9C';
  if (variant.startsWith('hurdle')) return '#9B59B6';
  if (variant === 'bench') return '#7F8C8D';
  return '#F39C12'; // cones / discs / gates
}

function goalWidthYd(variant: 'goal_mini' | 'goal_small' | 'goal_7v7' | 'goal_9v9' | 'goal_full'): number {
  switch (variant) {
    case 'goal_mini': return 1.2;
    case 'goal_small': return 1.8;
    case 'goal_7v7': return 4;
    case 'goal_9v9': return 5.5;
    case 'goal_full': return 8;
  }
}

function goalHeightYd(variant: 'goal_mini' | 'goal_small' | 'goal_7v7' | 'goal_9v9' | 'goal_full'): number {
  switch (variant) {
    case 'goal_mini': return 0.6;
    case 'goal_small': return 0.9;
    case 'goal_7v7': return 1.8;
    case 'goal_9v9': return 2.2;
    case 'goal_full': return 2.66;
  }
}
